import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { Button, Checkbox } from '@atlas/ui-primitives'
import type { ValueListRow } from '@/services/workfront-mappings.service'
import { humanize } from '../../components/StatusBadges'
import type { SelectableRows } from './FieldsTable'
import { ListState, type ListStateProps } from './ListState'
import { MatchBadge, RowStatusBadge } from './MappingBadges'

export interface ValuesTableProps extends SelectableRows, Pick<ListStateProps, 'isLoading' | 'isError' | 'onRetry'> {
	rows: ValueListRow[]
	filtered?: boolean
	/** A geography value ID → its label, for the scope column. */
	geographyLabel?: (valueId: string) => string | undefined
	onEdit: (row: ValueListRow) => void
}

function ValueRowView({
	row,
	selected,
	onToggle,
	onEdit,
	geographyLabel,
}: Omit<ValuesTableProps, 'rows'> & { row: ValueListRow }) {
	return (
		<TableRow>
			<TableCell>
				<Checkbox
					aria-label={`Select ${row.label}`}
					isSelected={selected.has(row.key)}
					onChange={() => onToggle(row.key)}
				/>
			</TableCell>
			<TableCell className="font-medium">{row.label}</TableCell>
			<TableCell>{humanize(row.axis)}</TableCell>
			<TableCell>
				{row.valueLabel ?? (row.valueId ? row.valueId : <span className="text-ds-danger">Unmatched</span>)}
			</TableCell>
			<TableCell>
				<MatchBadge match={row.match} />
			</TableCell>
			<TableCell>{row.waiting}</TableCell>
			<TableCell>{row.geoValueId ? (geographyLabel?.(row.geoValueId) ?? row.geoValueId) : 'everywhere'}</TableCell>
			<TableCell>
				<RowStatusBadge status={row.status} />
			</TableCell>
			<TableCell>
				<Button size="sm" variant="ghost" aria-label={`Edit ${row.label}`} onPress={() => onEdit(row)}>
					{row.valueId ? 'Edit' : 'Map'}
				</Button>
			</TableCell>
		</TableRow>
	)
}

/** The Workfront labels seen for the mapped axes: the ATLAS value each resolves to, or Unmatched, and what waits for it. */
export function ValuesTable({ rows, isLoading, isError, onRetry, filtered, ...rest }: ValuesTableProps) {
	return (
		<ListState
			noun="values"
			isLoading={isLoading}
			isError={isError}
			onRetry={onRetry}
			isEmpty={rows.length === 0}
			emptyTitle={filtered ? 'No labels match' : 'Every Workfront label in use is mapped'}
			emptyDescription={filtered ? 'Change the filters to see more.' : undefined}
		>
			<Table aria-label="Values">
				<TableHeader>
					<TableRow>
						{['', 'Workfront label', 'Axis', 'ATLAS value', 'Match', 'Waiting', 'Geography', 'Status', ''].map(
							(column, index) => (
								<TableHead key={`${column}-${index}`}>{column}</TableHead>
							),
						)}
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.map((row) => (
						<ValueRowView key={row.key} row={row} {...rest} />
					))}
				</TableBody>
			</Table>
		</ListState>
	)
}
